import { useEffect, useState } from "react";
import type { DateRange, GridPaginationModel } from "@act-group/act-ui-library";
import { useFetch } from "./useFetch";
import { fetchVerifications } from "../services/verificationsService";
import { DEFAULT_PAGE_SIZE } from "../constants/pagination";

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: DEFAULT_PAGE_SIZE };

const DATE_FORMAT = "YYYY-MM-DD";

export interface UseVerificationsOptions {
  dateRange: DateRange;
  status?: "Pending";
  /** Jurisdiction name to scope the list to; omit for all jurisdictions. */
  jurisdiction?: string;
  /**
   * Set to false to hold off fetching entirely — e.g. the Dashboard resolves
   * its default jurisdiction from a separate summary call, and fetching the
   * list before that lands would flash every jurisdiction's rows first, then
   * immediately refetch and swap to the filtered set once resolved. Defaults
   * to true so callers with nothing to wait on (e.g. the Pending page) are
   * unaffected.
   */
  ready?: boolean;
}

export function useVerifications({
  dateRange,
  status,
  jurisdiction,
  ready = true,
}: UseVerificationsOptions) {
  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);
  const { data, isLoading, error, fetchData } = useFetch(fetchVerifications);

  // A narrower filter can leave the grid on a page that no longer exists (page 6
  // of a now 2-page result set renders empty), so any filter change goes back to
  // the first page. Returning `current` unchanged when already there keeps this
  // from triggering a second render — and a second fetch — on every filter change.
  useEffect(() => {
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [dateRange, status, jurisdiction]);

  useEffect(() => {
    if (!ready) return;
    fetchData({
      // The grid's page index is 0-based; the portal API is 1-based.
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      fromDate: dateRange.start.format(DATE_FORMAT),
      toDate: dateRange.end.format(DATE_FORMAT),
      status,
      jurisdiction,
    }).catch(() => {
      // Failure is already captured in `error` state via useFetch; swallow
      // here so a network error doesn't surface as an unhandled rejection.
    });
  }, [fetchData, paginationModel, dateRange, status, jurisdiction, ready]);

  return {
    rows: data?.items ?? [],
    rowCount: data?.totalCount ?? 0,
    // Not ready yet means a fetch is about to happen once it is — report
    // loading so the grid shows its spinner instead of an empty flash.
    isLoading: !ready || isLoading,
    error,
    paginationModel,
    setPaginationModel,
  };
}
